import React, { useState, useEffect } from 'react';
import { 
  FiUser, 
  FiMail, 
  FiPhone, 
  FiMapPin, 
  FiHash, 
  FiBriefcase, 
  FiFileText, 
  FiSearch, 
  FiLoader,
  FiCheckCircle,
  FiAlertCircle
} from 'react-icons/fi';
import { INDIAN_STATES, getStateFromGSTIN, normalizeState } from '../../utils/indianStates';
import './AddModals.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const AddCustomerModal = ({ show, onClose, onSave, editingCustomer = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    gstin: '',
    state: '',
    address: '',
    company: '',
    notes: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidatingGST, setIsValidatingGST] = useState(false);
  const [gstValidationMessage, setGstValidationMessage] = useState('');

  // Initialize form data when editing or showing
  useEffect(() => {
    if (editingCustomer) {
      setFormData({
        name: editingCustomer.name || '',
        email: editingCustomer.email || '',
        phone: editingCustomer.phone || '',
        gstin: editingCustomer.gstin || '',
        state: editingCustomer.state || '',
        address: editingCustomer.address || '',
        company: editingCustomer.company || '',
        notes: editingCustomer.notes || ''
      });
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        gstin: '',
        state: '',
        address: '',
        company: '',
        notes: ''
      });
    }
    setErrors({});
    setGstValidationMessage('');
  }, [editingCustomer, show]);

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };

      // Auto-detect & auto-populate state when GSTIN is typed
      if (field === 'gstin') {
        const cleanGST = value.trim().toUpperCase();
        if (cleanGST.length >= 2) {
          const match = getStateFromGSTIN(cleanGST);
          if (match) {
            // Auto-fill state if empty or matching previous GST
            if (!prev.state || prev.state === '' || getStateFromGSTIN(prev.gstin)?.name === prev.state) {
              updated.state = match.name;
            }
          }
        }
      }

      return updated;
    });
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Clear GST validation message when GST field changes
    if (field === 'gstin') {
      setGstValidationMessage('');
    }
  };

  const validateGSTFormat = (gstin) => {
    const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstinRegex.test(gstin);
  };

  const handleGSTValidation = async () => {
    if (!formData.gstin.trim()) {
      setGstValidationMessage('Please enter a GSTIN to validate');
      return;
    }

    if (!validateGSTFormat(formData.gstin)) {
      setGstValidationMessage('Invalid GSTIN format. Please check and try again.');
      return;
    }

    setIsValidatingGST(true);
    setGstValidationMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/customers/validate-gst`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ gstin: formData.gstin })
      });

      if (response.ok) {
        const gstData = await response.json();
        
        // Extract state from GSTIN or API response
        let verifiedState = gstData.address?.state || '';
        if (!verifiedState) {
          const match = getStateFromGSTIN(formData.gstin);
          if (match) verifiedState = match.name;
        }

        // Auto-fill the form with GST data
        setFormData(prev => ({
          ...prev,
          name: gstData.legalName || prev.name,
          company: gstData.tradeName || prev.company,
          state: verifiedState || prev.state,
          address: `${gstData.address?.building || ''}, ${gstData.address?.street || ''}, ${gstData.address?.city || ''}, ${verifiedState} - ${gstData.address?.pincode || ''}`.trim().replace(/^,\s*/, '').replace(/,\s*$/, ''),
        }));

        setGstValidationMessage(`✅ GSTIN validated successfully! Auto-filled details for ${verifiedState}.`);
      } else {
        const errorData = await response.json();
        setGstValidationMessage(`❌ ${errorData.message || 'GSTIN validation failed'}`);
      }
    } catch (error) {
      console.error('GST validation error:', error);
      setGstValidationMessage('❌ Failed to validate GSTIN. Please try again.');
    } finally {
      setIsValidatingGST(false);
    }
  };

  // State verification status compared against GSTIN
  const getStateVerification = () => {
    if (!formData.gstin || formData.gstin.trim().length < 2) {
      if (formData.state) {
        return {
          type: 'selected',
          message: `State selected: ${formData.state} (Used for CGST/SGST vs IGST)`
        };
      }
      return null;
    }

    const gstinStateMatch = getStateFromGSTIN(formData.gstin);
    if (!gstinStateMatch) {
      return {
        type: 'invalid',
        message: 'Invalid state code in GSTIN'
      };
    }

    if (formData.state && normalizeState(formData.state) === normalizeState(gstinStateMatch.name)) {
      return {
        type: 'verified',
        message: `✓ State verified with GSTIN code: ${gstinStateMatch.code} - ${gstinStateMatch.name}`
      };
    } else if (formData.state) {
      return {
        type: 'mismatch',
        message: `⚠️ Note: Selected state (${formData.state}) differs from GSTIN state code (${gstinStateMatch.code} - ${gstinStateMatch.name}). Place of Supply will determine GST type.`
      };
    } else {
      return {
        type: 'suggested',
        message: `GSTIN belongs to: ${gstinStateMatch.code} - ${gstinStateMatch.name}`
      };
    }
  };

  const stateStatus = getStateVerification();

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Customer name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'This field is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'This field is required';
    } else if (!/^[\d\s\-+()]+$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (formData.gstin && !validateGSTFormat(formData.gstin)) {
      newErrors.gstin = 'Please enter a valid GSTIN format';
    }

    if (!formData.state.trim()) {
      newErrors.state = 'Please select a state to ensure proper CGST/SGST vs IGST tax application';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const dataToSend = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        gstin: formData.gstin.trim(),
        state: formData.state.trim(),
        address: formData.address.trim(),
        company: formData.company.trim(),
        notes: formData.notes.trim()
      };
      
      await onSave(dataToSend);
      handleClose();
    } catch (error) {
      console.error('Error saving customer:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      gstin: '',
      state: '',
      address: '',
      company: '',
      notes: ''
    });
    setErrors({});
    setIsSubmitting(false);
    setGstValidationMessage('');
    onClose();
  };

  if (!show) return null;

  return (
    <>
      <div className="custom-modal-backdrop" onClick={handleClose}></div>
      <div className="custom-modal add-customer-modal">
        <button className="close-btn" onClick={handleClose}>&times;</button>
        <h2>{editingCustomer ? 'Edit Customer' : 'Add New Customer'}</h2>
        
        <form onSubmit={handleSubmit} className="add-modal-form">
          {/* GST Field First */}
          <div className="form-group gst-section">
            <label className="form-label">
              <FiHash className="label-icon" />
              GST Number (Optional)
            </label>
            <div className="gst-input-group">
              <input
                type="text"
                value={formData.gstin}
                onChange={(e) => handleInputChange('gstin', e.target.value.toUpperCase())}
                className={`form-input ${errors.gstin ? 'error' : ''}`}
                placeholder="e.g., 27AAPFU0939F1ZV"
                disabled={isSubmitting}
                maxLength={15}
              />
              <button
                type="button"
                className="btn btn-secondary gst-validate-btn"
                onClick={handleGSTValidation}
                disabled={isValidatingGST || isSubmitting || !formData.gstin.trim()}
              >
                {isValidatingGST ? <FiLoader className="spinner" /> : <FiSearch />}
                {isValidatingGST ? 'Validating...' : 'Validate'}
              </button>
            </div>
            {errors.gstin && (
              <span className="error-message">{errors.gstin}</span>
            )}
            {gstValidationMessage && (
              <div className={`gst-message ${gstValidationMessage.includes('✅') ? 'success' : 'error'}`}>
                {gstValidationMessage}
              </div>
            )}
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">
                <FiUser className="label-icon" />
                Customer Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`form-input ${errors.name ? 'error' : ''}`}
                placeholder="Enter customer name"
                disabled={isSubmitting}
              />
              {errors.name && (
                <span className="error-message">{errors.name}</span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <FiBriefcase className="label-icon" />
                Company Name
              </label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
                className="form-input"
                placeholder="Company name (optional)"
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <FiMail className="label-icon" />
                Email Address *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`form-input ${errors.email ? 'error' : ''}`}
                placeholder="customer@example.com"
                disabled={isSubmitting}
              />
              {errors.email && (
                <span className="error-message">{errors.email}</span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <FiPhone className="label-icon" />
                Phone Number *
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={`form-input ${errors.phone ? 'error' : ''}`}
                placeholder="+91 98765 43210"
                disabled={isSubmitting}
              />
              {errors.phone && (
                <span className="error-message">{errors.phone}</span>
              )}
            </div>

            {/* State Selection & Verification */}
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label className="form-label">
                <FiMapPin className="label-icon" />
                State (Place of Supply for CGST/SGST vs IGST) *
              </label>
              <select
                value={formData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                className={`form-input form-select ${errors.state ? 'error' : ''}`}
                disabled={isSubmitting}
                style={{
                  height: '42px',
                  backgroundColor: '#fff',
                  border: errors.state ? '1px solid #dc3545' : '1px solid #ced4da',
                  borderRadius: '6px',
                  fontWeight: formData.state ? '600' : 'normal'
                }}
              >
                <option value="">-- Select State / Union Territory --</option>
                {INDIAN_STATES.map((s) => (
                  <option key={s.code} value={s.name}>
                    {s.code} - {s.name}
                  </option>
                ))}
              </select>
              {errors.state && (
                <span className="error-message">{errors.state}</span>
              )}
              {stateStatus && (
                <div style={{ 
                  marginTop: '6px', 
                  fontSize: '0.82rem', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '6px',
                  color: stateStatus.type === 'verified' ? '#2e7d32' : (stateStatus.type === 'mismatch' ? '#ed6c02' : '#1976d2'),
                  fontWeight: 500
                }}>
                  {stateStatus.type === 'verified' && <FiCheckCircle />}
                  {stateStatus.type === 'mismatch' && <FiAlertCircle />}
                  <span>{stateStatus.message}</span>
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <FiMapPin className="label-icon" />
              Address
            </label>
            <textarea
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              className="form-input form-textarea"
              placeholder="Enter customer address..."
              rows="3"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              <FiFileText className="label-icon" />
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              className="form-input form-textarea"
              placeholder="Additional notes about the customer..."
              rows="2"
              disabled={isSubmitting}
            />
          </div>

          <div className="modal-actions">
            <button 
              type="button" 
              className="btn btn-cancel" 
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={isSubmitting || !formData.name || !formData.email || !formData.phone || !formData.state}
            >
              {isSubmitting ? 'Saving...' : editingCustomer ? 'Update Customer' : 'Create Customer'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddCustomerModal;
